import { FC } from 'react';
import { Link } from '@remix-run/react';
import styled from 'styled-components';
import { Product } from '~/types/product';
import { Image } from '~/components/wrappers/Image';
import { Routes } from '~/common/constants/routes';
import { fontFamilies } from '~/theme/typography';
import {
  GlowInTheDarkBadge,
  hasGlowInTheDarkTag,
} from '~/components/GlowInTheDarkBadge';
import { trackSelectItem } from '~/common/utils/tracking';
import { TRACKING_LIST_IDS } from '~/common/constants/tracking';

interface InlineMatchWithProps {
  matching: Product[];
}

const InlineMatchWith: FC<InlineMatchWithProps> = ({ matching }) => {
  if (matching.length === 0) return null;

  return (
    <Wrapper>
      <Title>MATCH WITH</Title>
      <CardRow>
        {matching.map((product) => (
          <Card
            key={product.id}
            to={`${Routes.Product}/${product.handle}`}
            onClick={() =>
              trackSelectItem(product, TRACKING_LIST_IDS.pdpInlineMatchWith)
            }
          >
            <ImageWrapper>
              {product.images[0] && (
                <Image
                  src={`${product.images[0]}&width=200&format=auto&quality=100`}
                  srcSet={`${product.images[0]}&width=100&format=auto&quality=100 100w, ${product.images[0]}&width=150&format=auto&quality=100 150w, ${product.images[0]}&width=200&format=auto&quality=100 200w`}
                  sizes="75px"
                  alt={`${product.title} - Broken Planet`}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              )}
            </ImageWrapper>
            {hasGlowInTheDarkTag(product.tags) && (
              <BadgeOverlay>
                <GlowInTheDarkBadge compact />
              </BadgeOverlay>
            )}
          </Card>
        ))}
      </CardRow>
    </Wrapper>
  );
};

export default InlineMatchWith;

const Wrapper = styled.div`
  margin-top: 40px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`;

const Title = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 24px;
  font-style: normal;
  font-weight: 100;
  line-height: 24px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const CardRow = styled.div`
  display: flex;
  gap: 8px;
  padding-bottom: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Card = styled(Link)`
  position: relative;
  display: block;
  width: 75px;
  text-decoration: none;
  flex-shrink: 0;

  & img {
    object-fit: cover;
  }
`;

const ImageWrapper = styled.div`
  width: 75px;
  height: 100px;
  aspect-ratio: 3 / 4;
  overflow: hidden;
`;

const BadgeOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%);
  white-space: nowrap;
  pointer-events: none;
`;
